import axios from 'axios'
import { Message } from 'element-ui'
import store from '@/store'
import { getToken, setToken } from '@/utils/auth'

// 创建axios实例

const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API,
  timeout: 20000
})

// 登录失效处理标记，避免并发请求重复提示和重复跳转
let isUnauthorizedHandled = false

// 登录失效：清除本地登录态并回到登录页
function handleUnauthorized() {
  if (isUnauthorizedHandled) return
  isUnauthorizedHandled = true
  Message.closeAll()
  Message({
    message: '登录已过期，请重新登录',
    type: 'warning',
    duration: 3 * 1000
  })
  store.dispatch('user/resetToken').finally(() => {
    location.reload()
  })
}

// 续期请求的并发控制：同时刻只发一次 /auth/refresh，其余请求复用同一个 Promise
let refreshingPromise = null

// 用旧 Token 换发新 Token，成功后就地更新本地登录态
function refreshToken() {
  if (!refreshingPromise) {
    refreshingPromise = service({
      url: '/auth/refresh',
      method: 'post',
      _skipAuthRefresh: true
    }).then(res => {
      const newToken = res.data && res.data.token
      if (!newToken) {
        throw new Error('Token 续期失败')
      }
      setToken(newToken)
      store.commit('user/SET_TOKEN', newToken)
      return newToken
    }).finally(() => {
      refreshingPromise = null
    })
  }
  return refreshingPromise
}

// 登录接口的 401 表示账号密码错误（业务失败），不触发续期或退出登录
function isLoginRequest(config) {
  return !!config && typeof config.url === 'string' && config.url.indexOf('/auth/login') !== -1
}

// 登录态失效统一处理：先续期并重放原请求，续期失败才退出登录
function retryAfterRefresh(config, rejectValue) {
  // 登录失败：直接交给登录页提示
  if (isLoginRequest(config)) {
    return Promise.reject(rejectValue)
  }
  // 续期接口自身返回 401，或重放后的请求仍 401：说明会话已彻底失效
  if (!config || config._skipAuthRefresh || config._isRetried) {
    handleUnauthorized()
    return Promise.reject(rejectValue)
  }
  config._isRetried = true
  // 本请求发出后 Token 已被其它请求刷新过：直接用最新 Token 重放，无需再续期
  if (config._token && config._token !== getToken()) {
    return service(config)
  }
  return refreshToken().then(
    () => service(config),
    error => {
      // 续期本身失败（Token 非法/被禁用等）：直接退出登录
      handleUnauthorized()
      return Promise.reject(error)
    }
  )
}

// 请求拦截器
service.interceptors.request.use(
  config => {
    if (store.getters.token) {
      // 记录本次请求实际使用的 Token，便于 401 时判断是否已被其它请求刷新
      config._token = getToken()
      config.headers['Authorization'] = `Bearer ${config._token}`
    }
    return config
  },
  error => {
    console.log(error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    const res = response.data

    // 业务码 401：先尝试续期并重放原请求，失败才退出登录
    if (res.code === 401) {
      return retryAfterRefresh(response.config, new Error(res.message || '登录已过期'))
    }

    if (res.code !== 200) {
      Message({
        message: res.message || '错误',
        type: 'error',
        duration: 5 * 1000
      })
      return Promise.reject(new Error(res.message || '错误'))
    } else {
      return res
    }
  },
  error => {
    // HTTP 状态码 401：先尝试续期并重放原请求，失败才退出登录
    if (error.response && error.response.status === 401) {
      return retryAfterRefresh(error.config, error)
    }
    console.log('err' + error)
    Message({
      message: error.message,
      type: 'error',
      duration: 5 * 1000
    })
    return Promise.reject(error)
  }
)

export default service
